import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ConfirmationService, SelectItem } from '@openng/optimus-ui/api';
import { firstValueFrom, forkJoin, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  AREAS_CONTAINER,
  AREA_DETAIL_CONTAINER,
} from 'src/app/constants/common.constants';
import {
  AREA_TAB_PREFIX,
  NEW_AREA,
} from 'src/app/constants/location-constants';
import { TabService } from 'src/app/core/services/tab.service';
import { BreadCrumb } from 'src/app/model/common/bread-crumb';
import { ContactList } from 'src/app/model/contacts/contact-list';
import { Area } from 'src/app/model/locations/area';
import { AreaApiService } from 'src/app/services/api/area-api.service';
import { AreaBuilderService } from 'src/app/services/builder/area-builder.service';
import { RegionApiService } from 'src/app/services/api/region-api.service';
import { dynamicSort } from 'src/app/core/functions/common-functions';
import { isNullOrUndefined } from 'src/app/utils/utils';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { BreadCrumbBuilderService } from 'src/app/services/builder/breadcrumb-builder.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { ContactListApiService } from 'src/app/services/api/contact-list-api.service';
import { BaseContainer } from 'src/app/core/containers/base-container';
import { ContactApiService } from 'src/app/services/api/contact-api.service';
import { ContactBuilderService } from 'src/app/services/builder/contact-builder.service';
import { Contact } from 'src/app/model/contacts/contact';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { SelectChangeEvent, Select } from '@openng/optimus-ui/select';
import {
  AutoCompleteCompleteEvent,
  AutoComplete,
} from '@openng/optimus-ui/autocomplete';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { ScrollPanel } from '@openng/optimus-ui/scrollpanel';
import { InputText } from '@openng/optimus-ui/inputtext';
import {
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@openng/optimus-ui/tabs';
import { Ripple } from '@openng/optimus-ui/ripple';
import { ContactListSimpleViewContainerComponent } from '../../../contacts/containers/contact-list-simple-view/contact-list-simple-view-container.component';
import { ContactListSearchContainerComponent } from '../../../contacts/containers/contact-list-search/contact-list-search-container.component';
import { Panel } from '@openng/optimus-ui/panel';
import { Dialog } from '@openng/optimus-ui/dialog';
import { ContactListCreateContainerComponent } from '../../../contacts/containers/contact-list-create/contact-list-create-container.component';
import { Region } from 'src/app/model/locations/region';
import { BlockableDiv } from 'src/app/common/components/blockable-div/blockable-div.component';

@Component({
  selector: 'app-area-details-container',
  templateUrl: './area-details-container.component.html',
  styleUrls: ['./area-details-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ProgressSpinnerComponent,
    BlockUI,
    FormsModule,
    ReactiveFormsModule,
    Select,
    ButtonDirective,
    ScrollPanel,
    InputText,
    AutoComplete,
    Tabs,
    TabList,
    Ripple,
    Tab,
    TabPanels,
    TabPanel,
    ContactListSimpleViewContainerComponent,
    ContactListSearchContainerComponent,
    Panel,
    Dialog,
    ContactListCreateContainerComponent,
    TranslatePipe,
    BlockableDiv,
  ],
})
export class AreaDetailsContainerComponent
  extends BaseContainer
  implements OnInit, OnDestroy
{
  protected _translateService = inject(TranslateService);
  protected _breadCrumbService = inject(BreadCrumbBuilderService);
  protected _fb = inject(UntypedFormBuilder);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _confirmationService = inject(ConfirmationService);
  protected _contactListApi = inject(ContactListApiService);
  protected _authApi = inject(AuthApiService);
  protected _regionApi = inject(RegionApiService);
  protected _areaApi = inject(AreaApiService);
  protected _areaBuilder = inject(AreaBuilderService);
  protected _contactApi = inject(ContactApiService);
  protected _contactBuilder = inject(ContactBuilderService);
  protected _tabService = inject(TabService);

  private lId: string | null = null;
  @Input() set id(id) {
    this.lId = id;
  }
  get id() {
    return this.lId;
  }

  readonly index = input<number>();

  readonly changesEvent = output<any>();

  areaBreadCrumb?: BreadCrumb;
  backToSearchResult?: BreadCrumb;
  availableRegions: SelectItem[] = [];
  canCreate = false;
  canEdit = false;
  canDelete = false;
  tabClosingSub?: Subscription;
  breadCrumbSub?: Subscription;
  valueSub?: Subscription;
  area: Area | null = null;
  loading = true;
  saving = false;
  form?: UntypedFormGroup;
  contactList: ContactList | null = null;
  createContactListDialog = false;
  displayDialog = false;
  tempContactList: ContactList | null = null;
  actions: SelectItem[] = [];
  selectedAction: any = null;
  isEditing = false;
  tabIndex = 0;
  allContacts: Contact[] = [];
  filteredContacts: SelectItem[] = [];

  constructor() {
    super();
  }

  ngOnInit() {
    this.setupTabClosingSubscription();
    this.setupBreadCrumbSub();

    this.initialize();
  }

  setupBreadCrumbSub(): void {
    this.breadCrumbSub =
      this._breadCrumbService.breadCrumbSelectedEvent.subscribe(
        (breadCrumb) => {
          this._breadCrumbService.removeBreadCrumb(this.areaBreadCrumb!);
          const tab = this._tabService.buildNewTab(AREAS_CONTAINER, true);
          this._tabService.setMobileTab(tab);
        }
      );
  }

  initialize() {
    this.canCreate = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.LOCATIONS.AREAS.CREATE_AREA,
    ]);
    this.canEdit = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.LOCATIONS.AREAS.EDIT_AREA,
    ]);
    this.canDelete = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.LOCATIONS.AREAS.DELETE_AREA,
    ]);
    let observables = [];

    observables.push(
      this._regionApi.getRegions(),
      this._contactApi.getContacts()
    );
    if (this.id) {
      observables.push(this._areaApi.getArea(this.id));
    } else {
      // if there's no id, we need an empty area as our base
      this.area = {
        id: '',
        name: null,
        contactListId: null,
        owner: null,
        ownerId: null,
        facilities: [],
        region: null,
        regionId: null,
      };
    }

    forkJoin(observables)
      .pipe(take(1))
      .subscribe((responses: any[]) => {
        const regions: Region[] = [...responses[0]];
        this.availableRegions = [
          { label: 'Choose', value: null },
          ...regions
            .sort(dynamicSort('name', 1))
            .map((r) => ({ label: r.name!, value: r.id })),
        ];

        const contactDataClone = [...responses[1]];
        this.allContacts = contactDataClone
          .map((c) => this._contactBuilder.buildContact(c)!)
          .sort(dynamicSort('firstName', 1));

        if (responses.length > 2) {
          this.area = responses[2];
        }

        this.setupScreen();
      });
  }

  async setupScreen() {
    this.findContactList();
    this.buildForm();
    this.updateForm();

    if (this._deviceService.isMobile()) {
      this._translateService.get('LOCATION.SCREEN.AREA').subscribe((label) => {
        this._breadCrumbService.resetAndAddBreadCrumb(
          new BreadCrumb(label, null, true)
        );
        if (this.area && this.area.name) {
          this.areaBreadCrumb = new BreadCrumb(this.area.name, null, false);
        }
        this._breadCrumbService.addBreadCrumb(this.areaBreadCrumb!);
      });
    } else {
      const labels = await firstValueFrom(
        forkJoin({
          new: this._translateService.get('LOCATION.LABEL.NEW_AREA'),
          regularPrefix: this._translateService.get('LOCATION.SCREEN.AREA'),
        })
      );
      if (!this.area?.id) {
        this._tabService.updateActiveTabLabel(labels.new);
      } else {
        const tab = this._tabService.getTabById(this.area.id);
        this._tabService.updateTabLabel(
          tab!,
          labels.regularPrefix + ': ' + this.area?.name
        );
      }
    }

    this.updateActionsList();
    this.loading = false;
  }

  findContactList() {
    if (this.area?.contactListId) {
      this._contactListApi
        .getContactList(this.area.contactListId)
        .pipe(take(1))
        .subscribe({
          next: (list) => {
            this.contactList = list;
          },
          error: (error) => {
            this._logAndMessage.errorLogOnly(error);
            this._logAndMessage.translateToErrorAlert({
              headerKey: 'COMMON.MESSAGES.HEADERS.RETRIEVE_ERROR',
              bodyKey: 'CONTACT.MESSAGES.ERROR.RETRIEVE_CONTACT_LIST',
            });
          },
        });
    }
  }

  findOwner() {
    if (this.area?.ownerId) {
      return this.allContacts
        .filter((c) => c.id == this.area?.ownerId)
        .map((c) => ({ label: c.firstName + ' ' + c.lastName, value: c.id }))
        .shift();
    }
    return null;
  }

  buildForm() {
    this.form = this._fb.group({
      id: null,
      name: [null, Validators.required],
      contactListId: null,
      owner: null,
      region: [null, Validators.required],
      facilities: null,
      updater: [{ value: null, disabled: true }],
      version: null,
    });

    this.valueSub = this.form.valueChanges.subscribe((changes) => {
      if (this.form?.dirty) {
        this.changesEvent.emit({
          isDirty: true,
          index: this.index(),
          id: this.id,
        });
      }
    });
  }

  updateForm() {
    if (this.area) {
      this.form?.patchValue({
        id: this.area.id,
        name: this.area.name,
        contactListId: this.area.contactListId,
        owner: this.findOwner(),
        region: this.area.regionId,
        updater: this.area.updater,
        version: this.area.version,
      });

      if (!this.area.id) {
        this.isEditing = true;
      }

      if (!this.canEdit && !this.canCreate) {
        this.form?.disable();
      } else if (this.area.id && this.isEditing === false) {
        this.form?.disable();
      }
    }
  }

  updateActionsList() {
    if (this.canEdit || this.canDelete) {
      const selectAction$ = this._translateService.get(
        'COMMON.LABEL.SELECT_ACTION'
      );
      const removeList$ = this._translateService.get(
        'LOCATION.LABEL.BUTTONS.REMOVE_CONTACT_LIST'
      );
      const delete$ = this._translateService.get('COMMON.LABEL.BUTTONS.DELETE');

      let select,
        removeList,
        del = null;
      forkJoin([selectAction$, removeList$, delete$])
        .pipe(take(1))
        .subscribe((messages) => {
          select = messages[0];
          removeList = messages[1];
          del = messages[2];

          this.actions = [
            { label: select, value: null },
            { label: removeList, value: 'remove-list' },
          ];

          if (this.canDelete) {
            this.actions.push({ label: del, value: 'delete-area' });
          }

          // default actions here: Send to Day Crew, etc.
          this.actions = [...this.actions];
        });
    }
  }

  actionEvent($event: SelectChangeEvent, object?: any) {
    if ($event.value) {
      this.selectedAction = null;
      if ($event.value === 'remove-list') {
        this.clearContactList();
      } else if ($event.value === 'delete-area') {
        let confHeader = null,
          ok = null,
          cancel = null,
          message = null;
        const confHeader$ = this._translateService.get(
          'LOCATION.MESSAGES.HEADERS.DELETE_AREA'
        );
        const ok$ = this._translateService.get('COMMON.LABEL.BUTTONS.YES');
        const cancel$ = this._translateService.get('COMMON.LABEL.BUTTONS.NO');
        const message$ = this._translateService.get(
          'LOCATION.MESSAGES.CONFIRMATION.DELETE_AREA',
          { name: this.area?.name }
        );

        forkJoin([confHeader$, ok$, cancel$, message$]).subscribe(
          (messages) => {
            confHeader = messages[0];
            ok = messages[1];
            cancel = messages[2];
            message = messages[3];
          }
        );

        this._confirmationService.confirm({
          header: confHeader!,
          icon: 'fa fa-question-circle',
          acceptVisible: true,
          rejectVisible: true,
          acceptLabel: ok!,
          rejectLabel: cancel!,
          message: message!,
          accept: () => {
            this.saving = true;
            if (this.area && this.area.id) {
              this._areaApi.deleteArea(this.area.id).subscribe({
                next: (result) => {
                  this.saving = false;
                  this.displayDialog = false;
                },
                error: (error) => {
                  console.error('Error deleting area', error);
                  this._logAndMessage.translateToErrorAlert({
                    headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
                    bodyKey: 'LOCATION.MESSAGES.ERROR.DELETE_AREA',
                  });
                },
                complete: () => {
                  const index = this._tabService.getActiveIndex();
                  this._tabService.closeTab(index);
                },
              });
            }
          },
          reject: () => {},
        });
      }

      if (!isNullOrUndefined(object)) {
        object.clear(null);
      }
    }
  }

  ngOnDestroy(): void {
    if (this.breadCrumbSub) {
      this.breadCrumbSub.unsubscribe();
    }
  }

  saveArea(fromAction = false) {
    if (this.form?.valid || fromAction === true) {
      const areaForm = Object.assign({}, this.form?.getRawValue());
      const area = Object.assign(
        {},
        {
          name: areaForm.name,
          ownerId: areaForm.owner?.value ?? areaForm.owner,
          regionId: areaForm.region,
          contactListId: this.tempContactList
            ? this.tempContactList.id
            : this.area?.contactListId,
        }
      );

      if (!areaForm.id) {
        this.createArea(area);
      } else {
        this.updateArea(area);
      }
    } else {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'LOCATION.MESSAGES.HEADERS.SAVE_AREA',
        bodyKey: 'LOCATION.MESSAGES.ERROR.SAVING_AREA_ERROR',
      });
    }
  }

  createArea(area: any) {
    if (!this.canCreate) {
      return;
    }
    this.saving = true;

    area.updater = null;
    area.updatedAt = null;
    area.createdAt = null;
    const areaInput = Object.assign(
      {},
      {
        name: area.name,
        ownerId: area.ownerId,
        regionId: area.regionId,
        contactListId: area.contactListId,
      }
    );

    this._areaApi.createArea(areaInput).subscribe({
      next: (area) => {
        this._logAndMessage.translateToSuccessMessage({
          bodyKey: 'COMMON.MESSAGES.SUCCESS.SAVED_SUCCESSFULLY',
          headerKey: 'COMMON.MESSAGES.HEADERS.SUBMIT',
        });
        this.area = area;
        this.saving = false;
        this.findContactList();
        this.updateForm();
        this.form?.markAsPristine();
      },
      error: (error) => {
        console.error(error);
        this.saving = false;
        this.setEditing(false);
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'LOCATION.MESSAGES.HEADERS.SAVE_AREA',
          bodyKey: 'LOCATION.MESSAGES.ERROR.SAVING_AREA_ERROR',
        });
      },
      complete: () => {
        if (!this._deviceService.isMobile()) {
          const oldTab = this._tabService.getActiveTab();
          const tab = this._tabService.buildNewTab(
            AREA_DETAIL_CONTAINER,
            true,
            undefined,
            this.area!.id!
          );
          this._tabService.replaceTab(oldTab, tab);
          this.changesEvent.emit({
            isDirty: false,
            index: this.index(),
            id: this.id,
          });
        }
      },
    });
  }

  updateArea(area: any) {
    if (!this.canEdit) {
      return;
    }

    this.saving = true;

    const areaInput = Object.assign(
      {},
      {
        name: area.name,
        ownerId: area.ownerId,
        regionId: area.regionId,
        contactListId: area.contactListId,
      }
    );

    // If the contactlistid is pristine, that means that we haven't
    // removed it or changed it, and so it won't come in from getDirtyValues
    // but our lambda will remove the list if we don't send it back
    // so we need to re-send it every time we make other changes
    // to the list
    if (this.form?.controls['contactListId'].pristine) {
      areaInput['contactListId'] = this.form.controls['contactListId'].value;
    }

    if (!isNullOrUndefined(this.area)) {
      const update = this._areaBuilder.buildUpdateStatement(areaInput);

      this._areaApi.updateArea(this.area!.id!, update).subscribe({
        next: (area) => {
          this._logAndMessage.translateToSuccessMessage({
            bodyKey: 'COMMON.MESSAGES.SUCCESS.SAVED_SUCCESSFULLY',
            headerKey: 'COMMON.MESSAGES.HEADERS.SUBMIT',
          });
          this.area = area;
          if (areaInput['contactListId']) {
            this.findContactList();
          }
          this.updateForm();
          this.form?.markAsPristine();
          this.saving = false;
          this.setEditing(false);
        },
        error: (error) => {
          this.saving = false;
          this.setEditing(false);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'LOCATION.MESSAGES.HEADERS.SAVE_AREA',
            bodyKey: 'LOCATION.MESSAGES.ERROR.SAVING_AREA_ERROR',
          });
        },
        complete: () => {
          this.changesEvent.emit({
            isDirty: false,
            index: this.index(),
            id: this.id,
          });
        },
      });
    }
  }

  openCreateContactList() {
    this.createContactListDialog = true;
  }

  setTempList(list: ContactList) {
    if (this.canEdit === true) {
      this.setEditing(true);
      this.tempContactList = list;
      this.form?.patchValue({
        contactListId: !isNullOrUndefined(this.tempContactList)
          ? this.tempContactList.id
          : null,
      });
      this.form?.controls['contactListId'].markAsDirty();
      this.createContactListDialog = false;
    }
  }

  clearContactList() {
    if (this.canEdit === true) {
      this.setEditing(true);
      this.form?.patchValue({
        ContactListId: null,
      });
      this.form?.controls['contactListId'].markAsDirty();
      this.area!.contactListId = null;
      this.contactList = null;
      this.tempContactList = null;
    }
  }

  setEditing(editing: boolean) {
    if (!this.canCreate && !this.canEdit) {
      this._logAndMessage.translateToWarnMessage({
        bodyKey: 'COMMON.MESSAGES.WARN.ADMIN_RIGHTS',
        headerKey: 'COMMON.MESSAGES.HEADERS.AUTHORIZATION_MESSAGE',
      });
      return;
    }

    if (!editing) {
      this.isEditing = false;
      this.form?.disable();
    } else {
      this.isEditing = true;
      this.form?.enable();
      // always disabled
      this.form?.controls['updater'].disable();
    }
  }

  tabChanged($event: any): void {
    if (!isNullOrUndefined($event)) {
      this.tabIndex = $event.index;
    }
  }

  setupTabClosingSubscription(): void {
    this.tabClosingSub = this._tabService.attemptCloseEvent.subscribe(
      (closingTab) => {
        if (!isNullOrUndefined(this.id)) {
          if (this.id === closingTab.id && (!this.form || !this.form.dirty)) {
            this._tabService.closeTab(closingTab.index);
          } else if (
            this.id === closingTab.id &&
            this.form &&
            this.form.dirty
          ) {
            const unsaved$ = this._translateService.get(
              'COMMON.MESSAGES.CONFIRMATION.UNSAVED_CHANGES'
            );
            const yes$ = this._translateService.get(
              'COMMON.LABEL.BUTTONS.LEAVE'
            );
            const no$ = this._translateService.get(
              'COMMON.LABEL.BUTTONS.CANCEL'
            );
            const header$ = this._translateService.get(
              'COMMON.LABEL.HEADERS.LEAVE_PAGE'
            );
            forkJoin(unsaved$, yes$, no$, header$).subscribe((messages) => {
              this._confirmationService.confirm({
                header: messages[3],
                icon: 'fa fa-question-circle',
                acceptVisible: true,
                rejectVisible: true,
                acceptLabel: messages[1],
                rejectLabel: messages[2],
                message: messages[0],
                accept: () => {
                  if (!isNullOrUndefined(closingTab)) {
                    this._tabService.closeTab(closingTab.index);
                  }
                },
                reject: () => {
                  this._tabService.setActiveTab(closingTab.index);
                  this._tabService.revertTabChangeEvent.emit();
                },
              });
            });
          }
        }
      }
    );
  }

  contactSearch($event: AutoCompleteCompleteEvent) {
    if ($event.query) {
      this.filteredContacts = this.allContacts
        .filter((c) =>
          (c.firstName + ' ' + c.lastName)
            .toLowerCase()
            .includes($event.query.toLowerCase())
        )
        .filter((c: Contact) => c.inactiveAdAccount === false)
        .map((c) => ({ label: c.firstName + ' ' + c.lastName, value: c.id }));
    } else {
      this.filteredContacts = this.allContacts.map((c) => ({
        label: c.firstName + ' ' + c.lastName,
        value: c.id,
      }));
    }
  }
}
